import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import DataGrid, {
  Button as GridButton,
  Column,
  ColumnFixing,
  FilterRow,
  Paging,
  RemoteOperations,
  Scrolling,
  Toolbar,
  Item
} from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import {getDataSource} from '../../api/dataSource';
import MerchantPopup from './merchant-popup';
import RouterMerchantPopup from './router-merchant-popup';
import RouterProfilePopup from './router-profile-popup';
import './merchants.scss';

const GRID_COLUMNS_LS_KEY = 'grid:merchants:columns:v1';

const DEFAULT_COLUMN_WIDTHS = {
  ID: 80,
  Name: 250,
  Token: 150,
  CryptoNotifySignKey: 150,
  GatewayCardPaymentSystemTypeID: 150,
  IsGatewayCardPaymentAllow: 150,
  PublicKey: 150,
  IncomeRedirectUrl: 150,
  NotifyUrl: 150,
  CryptoNotifyUrl: 150,
  IsPaymentCascade: 150,
};

const loadColumnWidths = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return DEFAULT_COLUMN_WIDTHS;
    const raw = window.localStorage.getItem(GRID_COLUMNS_LS_KEY);
    if (!raw) return DEFAULT_COLUMN_WIDTHS;

    const parsed = JSON.parse(raw);
    const storedWidths = parsed && typeof parsed === 'object' ? parsed.widths : null;
    if (!storedWidths || typeof storedWidths !== 'object') return DEFAULT_COLUMN_WIDTHS;

    const allowedKeys = new Set(Object.keys(DEFAULT_COLUMN_WIDTHS));
    const sanitized = {};

    Object.keys(storedWidths).forEach((key) => {
      if (!allowedKeys.has(key)) return;
      const width = storedWidths[key];
      if (typeof width !== 'number' || !Number.isFinite(width)) return;
      if (width < 60 || width > 1000) return;
      sanitized[key] = width;
    });

    const merged = {...DEFAULT_COLUMN_WIDTHS, ...sanitized};

    const hadUnknownKeys = Object.keys(storedWidths).some((key) => !allowedKeys.has(key));
    if (hadUnknownKeys) {
      window.localStorage.setItem(GRID_COLUMNS_LS_KEY, JSON.stringify({widths: sanitized}));
    }

    return merged;
  } catch (e) {
    return DEFAULT_COLUMN_WIDTHS;
  }
};

const saveColumnWidths = (widths) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(GRID_COLUMNS_LS_KEY, JSON.stringify({widths}));
  } catch (e) {
    // ignore
  }
};

const hasValue = (value) => value !== null && value !== undefined && value !== '';

const toBool = (value) => value === true || value === 1 || value === '1';

const Merchants = (props) => {
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const [columnWidths, setColumnWidths] = useState(loadColumnWidths);

  const haveEnoughRight = useMemo(() => {
    const {userRights} = props;
    return userRights.includes('11');
  }, []);

  // Popup state
  const [popup, setPopup] = useState(null); // null | { mode: 'view'|'edit'|'add', data: object|null }
  const [routerMerchant, setRouterMerchant] = useState(null);
  const [isRouterProfileVisible, setIsRouterProfileVisible] = useState(false);

  const openPopup = useCallback((mode, data = null) => {
    setPopup({mode, data});
  }, []);

  const closePopup = useCallback(() => {
    setPopup(null);
  }, []);

  const openRouterMerchantPopup = useCallback((merchant) => {
    setRouterMerchant(merchant || null);
  }, []);

  const closeRouterMerchantPopup = useCallback(() => {
    setRouterMerchant(null);
  }, []);

  const refreshGrid = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.instance().refresh();
    }
  }, []);

  const onSuccess = useCallback(() => {
    refreshGrid();
  }, [refreshGrid]);

  const dataSource = useMemo(
    () =>
      getDataSource({
        key: 'ID',
        object: 'Merchant',
        columns: ['ID', 'Name', 'Token', 'CryptoNotifySignKey', 'NotifyUrl', 'PublicKey', 'IncomeRedirectUrl',
          'IsGatewayCardPaymentAllow', 'GatewayCardPaymentSystemTypeID', 'CryptoNotifyUrl', 'IsPaymentCascade'],
        sorts: ['ID'],
        loadAll: true,
      }),
    [],
  );

  const onGridOptionChanged = useCallback(
    (e) => {
      if (!e?.fullName || typeof e.fullName !== 'string') return;
      if (!e.fullName.endsWith('.width')) return;
      if (!e.fullName.startsWith('columns[')) return;
      if (!e.component) return;

      const nextWidths = {};
      Object.keys(DEFAULT_COLUMN_WIDTHS).forEach((dataField) => {
        const width = e.component.columnOption(dataField, 'width');
        if (typeof width === 'number' && Number.isFinite(width)) {
          nextWidths[dataField] = width;
        }
      });

      if (!Object.keys(nextWidths).length) return;

      setColumnWidths((prev) => {
        let changed = false;
        Object.keys(DEFAULT_COLUMN_WIDTHS).forEach((dataField) => {
          if (prev[dataField] !== nextWidths[dataField]) changed = true;
        });
        if (!changed) return prev;

        const merged = {...prev, ...nextWidths};
        saveColumnWidths(merged);
        return merged;
      });
    },
    [setColumnWidths],
  );

  const resetColumns = () => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(GRID_COLUMNS_LS_KEY);
      }
    } catch (e) {
      // ignore
    }
    setColumnWidths(DEFAULT_COLUMN_WIDTHS);
  };

  const navigateToSchemePS = useCallback((merchantId) => {
    try {
      window.localStorage.setItem('filter:merchants-scheme-ps:merchantId', String(merchantId));
    } catch (e) {
      // ignore
    }
    navigate('/merchants-scheme-ps');
  }, [navigate]);

  return (
    <React.Fragment>
      <h4 className={'content-block grid-title'}>Мерчанты</h4>
      <div className={'content-block grid-wrapper merchants-wrapper'}>
        <DataGrid
          ref={gridRef}
          dataSource={dataSource}
          keyExpr={'ID'}
          repaintChangesOnly={true}
          hoverStateEnabled={true}
          height={'100%'}
          width={'100%'}
          wordWrapEnabled={true}
          allowColumnResizing={true}
          columnResizingMode={'widget'}
          columnAutoWidth={false}
          onOptionChanged={onGridOptionChanged}
        >
          <Toolbar>
            <Item location={'before'}>
              <Button text={'Обновить'} onClick={refreshGrid}/>
            </Item>
            <Item location={'before'}>
              <Button
                text={'Очистить фильтр'}
                onClick={() => {
                  if (gridRef.current) {
                    gridRef.current.instance().clearFilter();
                  }
                }}
              />
            </Item>
            <Item location={'before'}>
              <Button text={'Сбросить ширины'} onClick={resetColumns}/>
            </Item>
            <Item location={'before'}>
              <Button
                text={'Роутинг'}
                onClick={() => setIsRouterProfileVisible(true)}
              />
            </Item>
            <Item location="after">
              <Button
                visible={haveEnoughRight}
                text="Добавить"
                icon="add"
                onClick={() => openPopup('add')}
              />
            </Item>
          </Toolbar>

          <RemoteOperations filtering={false} paging={false} sorting={false}/>
          <Paging enabled={false}/>
          <Scrolling mode={'infinite'} showScrollbar={'always'}/>
          <ColumnFixing enabled={true}/>
          <FilterRow visible={true}/>

          <Column dataField="ID" caption="ID" dataType="number" width={columnWidths.ID}/>

          <Column dataField="Name" caption="Название" width={columnWidths.Name}/>

          <Column
            dataField="Token"
            caption="Ключ"
            dataType="boolean"
            alignment="left"
            width={columnWidths.Token}
            calculateCellValue={(row) => hasValue(row.Token)}
          />

          <Column
            dataField="CryptoNotifySignKey"
            caption="Крипто ключ"
            dataType="boolean"
            alignment="left"
            width={columnWidths.CryptoNotifySignKey}
            calculateCellValue={(row) => hasValue(row.CryptoNotifySignKey)}
          />

          <Column
            dataField="GatewayCardPaymentSystemTypeID"
            caption="Карточная система"
            dataType="boolean"
            alignment="left"
            width={columnWidths.GatewayCardPaymentSystemTypeID}
            calculateCellValue={(row) => hasValue(row.GatewayCardPaymentSystemTypeID)}
          />

          <Column
            dataField="IsGatewayCardPaymentAllow"
            caption="Карты: Н2Н Режим"
            dataType="boolean"
            alignment="left"
            width={columnWidths.IsGatewayCardPaymentAllow}
            calculateCellValue={(row) => toBool(row.IsGatewayCardPaymentAllow)}
          />

          <Column
            dataField="PublicKey"
            caption="Публичный ключ"
            dataType="boolean"
            alignment="left"
            width={columnWidths.PublicKey}
            calculateCellValue={(row) => hasValue(row.PublicKey)}
          />

          <Column
            dataField="IncomeRedirectUrl"
            caption="Редирект URL"
            dataType="boolean"
            alignment="left"
            width={columnWidths.IncomeRedirectUrl}
            calculateCellValue={(row) => hasValue(row.IncomeRedirectUrl)}
          />

          <Column
            dataField="NotifyUrl"
            caption="Статус URL"
            dataType="boolean"
            alignment="left"
            width={columnWidths.NotifyUrl}
            calculateCellValue={(row) => hasValue(row.NotifyUrl)}
          />

          <Column
            dataField="CryptoNotifyUrl"
            caption="Крипто статус URL"
            dataType="boolean"
            alignment="left"
            width={columnWidths.CryptoNotifyUrl}
            calculateCellValue={(row) => hasValue(row.CryptoNotifyUrl)}
          />

          <Column
            dataField="IsPaymentCascade"
            caption="Каскадинг"
            dataType="boolean"
            alignment="left"
            width={columnWidths.IsPaymentCascade}
            calculateCellValue={(row) => toBool(row.IsPaymentCascade)}
          />

          <Column type="buttons" fixed={true} fixedPosition="right" width={150}>
            <GridButton
              hint="Просмотр"
              icon="search"
              onClick={(e) => openPopup('view', e.row.data)}
            />
            <GridButton
              visible={haveEnoughRight}
              hint="Редактировать"
              icon="edit"
              onClick={(e) => openPopup('edit', e.row.data)}
            />
            <GridButton
              hint="Схемы ПС"
              icon="link"
              onClick={(e) => navigateToSchemePS(e.row.data.ID)}
            />
            <GridButton
              hint="Router"
              icon="mdi mdi-call-split"
              onClick={(e) => openRouterMerchantPopup(e.row.data)}
            />
          </Column>
        </DataGrid>
      </div>

      {popup && (
        <MerchantPopup
          mode={popup.mode}
          data={popup.data}
          haveEnoughRight={haveEnoughRight}
          onClose={closePopup}
          onSuccess={onSuccess}
        />
      )}

      {routerMerchant && (
        <RouterMerchantPopup
          merchant={routerMerchant}
          haveEnoughRight={haveEnoughRight}
          onClose={closeRouterMerchantPopup}
        />
      )}

      {isRouterProfileVisible && (
        <RouterProfilePopup
          haveEnoughRight={haveEnoughRight}
          onClose={() => setIsRouterProfileVisible(false)}
        />
      )}
    </React.Fragment>
  );
};

export default Merchants;
